import http from './http';

import { createLogger } from '../utils/logger';

const logger = createLogger('homeInventory');
export interface HomeInventoryInfoRec {
  id: string;
  userId: string;
  itemName?: string;
  category?: string;
  brand?: string;
  model?: string;
  serialNumber?: string;
  purchaseDate?: string;
  purchasePrice?: number;
  currentValue?: number;
  condition?: string;
  locationInHome?: string;
  warrantyExpiration?: string;
  notes?: string;
  isLocked?: boolean;
}

class HomeInventoryInfoService {
  async getAllHomeInventoryInfo(): Promise<HomeInventoryInfoRec[]> {
    const response = await http.get('/api/home-inventory-info');
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch home inventory information');
    }

    return response.data.data || [];
  }

  async getHomeInventoryInfoById(id: string): Promise<HomeInventoryInfoRec | null> {
    try {
      const response = await http.get(`/api/home-inventory-info/${id}`);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch home inventory information');
      }

      return response.data.data || null;
    } catch (error) {
      logger.error('Error fetching home inventory information by ID:', error);
      return null;
    }
  }

  async createHomeInventoryInfo(homeInventoryData: Partial<HomeInventoryInfoRec>): Promise<HomeInventoryInfoRec> {
    const response = await http.post('/api/home-inventory-info', homeInventoryData);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to save home inventory information');
    }

    return response.data.data;
  }

  async updateHomeInventoryInfo(id: string, homeInventoryData: Partial<HomeInventoryInfoRec>): Promise<HomeInventoryInfoRec> {
    const response = await http.patch(`/api/home-inventory-info/${id}`, homeInventoryData);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update home inventory information');
    }

    return response.data.data;
  }

  async deleteHomeInventoryInfo(id: string): Promise<void> {
    const response = await http.delete(`/api/home-inventory-info/${id}`);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete home inventory information');
    }
  }

  async lockHomeInventoryInfo(id: string): Promise<void> {
    logger.debug('[Service Debug] Calling lock API for home inventory ID:', id);
    const response = await http.post(`/api/home-inventory-info/${id}/lock`);
    logger.debug('[Service Debug] Lock API response:', response.data);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to lock home inventory information');
    }
  }

  async unlockHomeInventoryInfo(id: string, granteeEmails: string[]): Promise<void> {
    logger.debug('[HomeInventory Service] Calling unlock API - id:', id, 'granteeEmails:', granteeEmails);
    const response = await http.post(`/api/home-inventory-info/${id}/unlock`, { granteeEmails });
    logger.debug('[HomeInventory Service] Unlock API response:', response.data);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to unlock home inventory information');
    }
  }

  async getHomeInventoryAccessGrants(id: string): Promise<any[]> {
    const response = await http.get(`/api/home-inventory-info/${id}/access-grants`);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch access grants');
    }

    return response.data.data || [];
  }

  // Photo management methods
  async uploadPhoto(id: string, base64: string, mimeType?: string): Promise<{ key: string; url: string }> {
    logger.debug('[HomeInventory Service] Uploading photo for id:', id);
    const response = await http.post(`/api/home-inventory-info/${id}/photos`, {
      base64,
      mimeType: mimeType || 'image/jpeg',
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to upload photo');
    }

    return response.data.data;
  }

  async deletePhoto(id: string, photoKey: string): Promise<void> {
    logger.debug('[HomeInventory Service] Deleting photo:', photoKey);
    const encodedKey = encodeURIComponent(photoKey);
    const response = await http.delete(`/api/home-inventory-info/${id}/photos/${encodedKey}`);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete photo');
    }
  }

  async getPhotos(id: string): Promise<Array<{ key: string; url: string }>> {
    const response = await http.get(`/api/home-inventory-info/${id}/photos`);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch photos');
    }

    return response.data.data || [];
  }
}

export const homeInventoryInfoService = new HomeInventoryInfoService();
export default homeInventoryInfoService;